'use client'

import Image from 'next/image'
import { Music, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

interface SongArtworkProps {
  src: string | null | undefined
  alt?: string
  size: number
  className?: string
  fallbackClassName?: string
  /** Icon to show when no artwork is provided. Defaults to Music note. */
  fallbackIcon?: LucideIcon
}

// Domains we've configured in next.config.ts for optimization
const OPTIMIZED_DOMAINS = [
  'mzstatic.com',        // iTunes artwork CDN
  'images.unsplash.com', // Unsplash (landing page)
  's3.amazonaws.com',    // AWS S3 buckets (*.s3.*.amazonaws.com)
  'placehold.co',        // Dev mock merch images
]

/**
 * Check if a URL is from a domain we can optimize with Next.js Image
 */
function isOptimizedDomain(url: string): boolean {
  try {
    const hostname = new URL(url).hostname
    return OPTIMIZED_DOMAINS.some(domain => hostname.endsWith(domain))
  } catch {
    return false
  }
}

/**
 * Check if a URL is a local/relative path (uploads, public folder)
 */
function isLocalPath(url: string): boolean {
  return url.startsWith('/') || url.startsWith('./') || url.startsWith('../')
}

/**
 * Smart artwork component that uses Next.js Image for optimized domains
 * and falls back to <img> for external/custom artwork URLs.
 * 
 * Shows a music icon placeholder when no src is provided.
 */
export function SongArtwork({
  src,
  alt = '',
  size,
  className,
  fallbackClassName,
  fallbackIcon: FallbackIcon = Music,
}: SongArtworkProps) {
  const baseClasses = cn("object-cover", className)

  // No source - show fallback
  if (!src) {
    return (
      <div
        className={cn(
          "flex items-center justify-center bg-zinc-800",
          fallbackClassName || className
        )}
        style={{ width: size, height: size }}
      >
        <FallbackIcon className="text-zinc-600" style={{ width: size * 0.5, height: size * 0.5 }} />
      </div>
    )
  }

  // Local paths or optimized domains - use Next.js Image
  if (isLocalPath(src) || isOptimizedDomain(src)) {
    return (
      <Image 
        src={src} 
        alt={alt} 
        width={size} 
        height={size} 
        className={baseClasses}
      />
    )
  }

  // External/custom artwork - can't optimize, use regular img
  return (
    // eslint-disable-next-line @next/next/no-img-element -- External domain not in allowed list
    <img 
      src={src} 
      alt={alt} 
      width={size} 
      height={size} 
      className={baseClasses}
    />
  )
}

